import { isLgwanAccess, isOnlyLocalhost } from '@/shared/lib/system';

/**
 * リダイレクトURLの検証
 *
 * オープンリダイレクト攻撃を防ぐため、同一オリジンのURLのみを許可します。
 *
 * @param url - 検証するURL（相対パスまたは絶対URL）
 * @returns 検証済みの安全なURL（相対パス）、無効な場合は null
 *
 * @example
 * ```typescript
 * // 相対パス（許可）
 * validateRedirectUrl('/dashboard') // '/dashboard'
 * validateRedirectUrl('/projects?page=2') // '/projects?page=2'
 *
 * // 同一オリジンの絶対URL（許可）
 * validateRedirectUrl('https://example.com/dashboard') // '/dashboard' (現在のオリジンと一致する場合)
 *
 * // 外部URL（拒否）
 * validateRedirectUrl('https://evil.com/phishing') // null
 *
 * // プロトコル相対URL（拒否）
 * validateRedirectUrl('//evil.com/phishing') // null
 * ```
 */
export const validateRedirectUrl = (url?: string): string | null => {
  if (!url) return null;

  try {
    // 相対パスの場合はそのまま許可
    if (url.startsWith('/') && !url.startsWith('//')) {
      // パスインジェクションを防ぐ（連続するスラッシュを1つに正規化）
      const cleanPath = url.replace(/\/+/g, '/');
      return cleanPath;
    }

    // 絶対URLの場合はオリジンをチェック
    const urlObj = new URL(url, window.location.origin);
    if (urlObj.origin === window.location.origin) {
      // 同一オリジンの場合は相対パスとして返す
      return urlObj.pathname + urlObj.search;
    }

    // 異なるオリジンの場合は拒否
    if (import.meta.env.DEV) {
      // biome-ignore lint/suspicious/noConsole: Development logging
      console.warn('Rejected redirect to external URL:', url);
    }
    return null;
  } catch (_e) {
    // 無効なURLの場合は拒否
    if (import.meta.env.DEV) {
      // biome-ignore lint/suspicious/noConsole: Development logging
      console.warn('Invalid redirect URL:', url);
    }
    return null;
  }
};

/**
 * 与えられたURLを接続方法に応じたホスト名へ置き換える。
 *
 * - LGWAN / AWS Direct Connect などプロキシ必須環境下では location.host を採用
 * - ローカル開発時はリバースプロキシ経由での接続を想定し localhost:80 を利用
 *
 * @param url 変換対象のURL
 * @param useProxy 開発時でも強制的にプロキシ経由で接続するか
 */
export const replaceLocationHost = (url: string, useProxy = false): string => {
  const newUrl = new URL(url);

  if (isLgwanAccess() || isOnlyLocalhost() || useProxy) {
    // ローカル開発時はVite dev serverのプロキシを経由（CORS回避のため）
    newUrl.host = location.host;
    newUrl.protocol = location.protocol;
  }

  return newUrl.href;
};

/**
 * S3署名付きURLをCloudFront経由のプロキシURLに変換する。
 *
 * 動作検証を容易にするため、現在は接続元にかかわらず一律で
 * CloudFrontの /dnr/attachments/ パスを経由してS3にプロキシする。
 * 署名付きクエリパラメータはそのまま保持される。
 *
 * @param presignedUrl S3署名付きURL
 * @returns プロキシ経由のURL
 */
export const toUploadProxyUrl = (presignedUrl: string): string => {
  const url = new URL(presignedUrl);
  const s3Path = url.pathname;
  const query = url.search;

  // TODO(ITA-1739): WAF などの動作検証後に、LGWAN/ローカルのみプロキシする条件分岐へ戻すことを検討する。
  // ローカル開発時はVite dev serverのプロキシを経由（CORS回避のため）
  return `${location.protocol}//${location.host}/dnr/attachments${s3Path}${query}`;
};

/**
 * 外部URLを新しいタブで開く。
 *
 * `noopener,noreferrer` を付与してタブナッピング攻撃を防ぐ。
 * 外部リンクを開く際は素の `window.open` ではなくこのヘルパーを使用すること。
 */
export const openExternalUrl = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * サニタイズ済み添付ファイルURLをローカル開発環境向けに書き換える。
 *
 * LGWAN上ではそのまま返し、ローカルのみ localhost:80 に張り替える。
 */
export const replaceLocationHostForSanitizedAttachment = (url: string): string => {
  const newUrl = new URL(url);

  if (isOnlyLocalhost()) {
    // ローカル開発時はVite dev serverのプロキシを経由（CORS回避のため）
    newUrl.host = location.host;
    newUrl.protocol = location.protocol;
  }

  return newUrl.href;
};
